import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export function PortalTooltip({ children, content, className = "inline-flex items-center justify-center cursor-help", contentClassName = "w-64 text-left", disabled = false }: { children: React.ReactNode, content: React.ReactNode, className?: string, contentClassName?: string, disabled?: boolean }) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  let rafId: number | null = null;
  const updatePosition = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      if (triggerRef.current && tooltipRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        // Calculate position so the bottom of the tooltip wrapper touches the top of the trigger
        setCoords({
          left: rect.left + rect.width / 2 - tooltipRect.width / 2,
          top: rect.top - tooltipRect.height,
        });
      }
      rafId = null;
    });
  };

  useEffect(() => {
    if (!disabled && isVisible) {
      // Need a small delay to allow DOM to render and get accurate dimensions
      const timer = setTimeout(updatePosition, 0);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else if (disabled && isVisible) {
      setIsVisible(false);
    }
  }, [isVisible, disabled]);

  return (
    <div 
      ref={triggerRef}
      onMouseEnter={() => !disabled && setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      className={className}
    >
      {children}
      {!disabled && isVisible && createPortal(
        <div 
          ref={tooltipRef}
          onMouseEnter={() => !disabled && setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
          className="pb-2" // Important: 8px invisible padding at the bottom bridges the gap to the icon
          style={{ 
            position: 'fixed', 
            top: coords.top, 
            left: coords.left,
            zIndex: 99999,
            // Only make it visible once position is calculated, to avoid flash at top-left
            opacity: coords.top === 0 ? 0 : 1,
            pointerEvents: 'auto'
          }}
        >
          <div className={`relative p-3 bg-slate-950 dark:bg-slate-950 text-white text-[10px] sm:text-xs rounded-xl dark:shadow-2xl ring-1 ring-slate-900/10 dark:ring-white/10 normal-case tracking-normal font-normal ${contentClassName}`}>
            {content}
            <div className="absolute -bottom-[8px] left-1/2 -translate-x-1/2 border-[8px] border-transparent border-t-slate-950 dark:border-t-slate-950 border-b-0 border-x-[8px] border-x-transparent drop-shadow-sm"></div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
